import { Download, MessageSquareText, Trash2, Users } from 'lucide-react';
import { StatTile } from '../../portal';
import { formatCompactNumber } from './overviewSummary';

export interface OverviewAnalytics {
  installs: number | null;
  uninstalls: number | null;
  latestVersionPercent: number | null;
}

interface OverviewStatsProps {
  liveVersionName: string | null;
  /** null = analytics not available for this viewer/environment. */
  analytics: OverviewAnalytics | null;
  totalReviews: number | null;
  reviewsThisWeek: number | null;
  isLoading: boolean;
}

/**
 * Keyed on the text so a number fades in when it first lands or changes
 * (analytics arriving after the tiles) but not on identical refetches.
 */
function fadeValue(text: string) {
  return (
    <span key={text} className="motion-enter inline-block">
      {text}
    </span>
  );
}

const UNAVAILABLE = {
  value: fadeValue('—'),
  caption: 'Not available yet',
  valueClassName: 'text-seeker-400',
};

function analyticsTile(value: number | null) {
  return value == null
    ? UNAVAILABLE
    : { value: fadeValue(formatCompactNumber(value)), caption: undefined };
}

export function OverviewStats({
  liveVersionName,
  analytics,
  totalReviews,
  reviewsThisWeek,
  isLoading,
}: OverviewStatsProps) {
  const latest = analytics?.latestVersionPercent;

  return (
    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
      <StatTile
        icon={<Users />}
        label={
          liveVersionName
            ? `On latest Version (v${liveVersionName})`
            : 'On latest Version'
        }
        isLoading={isLoading}
        {...(latest == null
          ? UNAVAILABLE
          : {
              value: fadeValue(`${latest.toFixed(1)}%`),
              valueClassName: 'text-green-400',
            })}
      />
      <StatTile
        icon={<Download />}
        label="Installs"
        isLoading={isLoading}
        {...analyticsTile(analytics?.installs ?? null)}
      />
      <StatTile
        icon={<Trash2 />}
        label="Uninstalls"
        isLoading={isLoading}
        {...analyticsTile(analytics?.uninstalls ?? null)}
      />
      <StatTile
        icon={<MessageSquareText />}
        label="Reviews"
        isLoading={isLoading}
        {...(totalReviews == null
          ? UNAVAILABLE
          : {
              value: fadeValue(formatCompactNumber(totalReviews)),
              caption: reviewsThisWeek ? `${reviewsThisWeek} this week` : null,
            })}
      />
    </div>
  );
}
